import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../../services/api';
import alertService from '../../../utils/alert';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/currency';
import SearchableSelect from '../SearchableSelect';
import BankAccountFields from './BankAccountFields';

const INPUT = 'w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3]';
const LABEL = 'block text-[11px] font-semibold text-[#706E6B] mb-1';
const METHODS = [['bank_transfer', 'Bank transfer'], ['upi', 'UPI'], ['cash', 'Cash'], ['cheque', 'Cheque'], ['card', 'Card'], ['other', 'Other']];
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// One receipt from a customer, split across any of their unpaid invoices.
export default function ReceiptModal({ accounts, contacts, preset, onClose, onSaved }) {
  const [kind, setKind] = useState(preset?.contactId && !preset?.accountId ? 'contact' : 'account');
  const [customerId, setCustomerId] = useState(preset?.accountId || preset?.contactId || '');
  const [invoices, setInvoices] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [spread, setSpread] = useState('');
  const [advance, setAdvance] = useState('');
  const [meta, setMeta] = useState({ date: localToday(), method: 'bank_transfer', reference: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const options = kind === 'account'
    ? accounts.map((a) => ({ _id: a._id, name: a.name }))
    : contacts.map((c) => ({ _id: c._id, name: c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') }));

  useEffect(() => {
    if (!customerId) { setInvoices([]); return; }
    api.get('/crm/receipts/open-invoices', { params: { [kind === 'account' ? 'accountId' : 'contactId']: customerId } })
      .then((res) => {
        const list = res.data.invoices || [];
        setInvoices(list);
        if (preset?.invoiceId) {
          const inv = list.find((i) => i._id === preset.invoiceId);
          if (inv) setAlloc({ [inv._id]: String(inv.nextInstallment?.amountDue ?? r2(inv.totalAmount - inv.amountPaid)) });
        }
      })
      .catch(() => setInvoices([]));
  }, [customerId, kind, preset?.invoiceId]);

  const balance = (i) => r2(i.totalAmount - (i.amountPaid || 0));
  const allocated = r2(Object.values(alloc).reduce((s, v) => s + (Number(v) || 0), 0));
  const total = r2(allocated + (Number(advance) || 0));

  const applySpread = () => {
    let left = Number(spread) || 0;
    const next = {};
    invoices.forEach((i) => {
      const take = Math.min(left, balance(i));
      if (take > 0) { next[i._id] = String(r2(take)); left = r2(left - take); }
    });
    setAlloc(next);
    setAdvance(left > 0 ? String(r2(left)) : '');
  };

  const save = async () => {
    const allocations = Object.entries(alloc).map(([invoiceId, amount]) => ({ invoiceId, amount: Number(amount) })).filter((a) => a.amount > 0);
    if (!allocations.length && !(Number(advance) > 0)) return alertService.error('Enter an amount against an invoice, or keep it as an advance');
    setSaving(true);
    try {
      await api.post('/crm/receipts', { [kind === 'account' ? 'accountId' : 'contactId']: customerId, ...meta, tdsAmount: Number(meta.tdsAmount) || 0, allocations, advanceAmount: Number(advance) || 0 });
      onSaved();
    } catch (e) {
      alertService.error('Receipt not recorded', e.response?.data?.message || 'Please try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-start justify-center overflow-y-auto sm:p-4" onMouseDown={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-4xl sm:my-8" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">Record receipt</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[#F3F2F2]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className={LABEL} htmlFor="rc-kind">Received from</label>
              <select id="rc-kind" className={INPUT} value={kind} onChange={(e) => { setKind(e.target.value); setCustomerId(''); setAlloc({}); }}>
                <option value="account">Account</option><option value="contact">Contact</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <span className={LABEL}>{kind === 'account' ? 'Account' : 'Contact'}</span>
              <SearchableSelect value={customerId} onChange={(v) => { setCustomerId(v); setAlloc({}); }} options={options} placeholder="Choose customer…" />
            </div>
            <div><label className={LABEL} htmlFor="rc-date">Date received</label><input id="rc-date" type="date" className={INPUT} value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} /></div>
            <div>
              <label className={LABEL} htmlFor="rc-method">Method</label>
              <select id="rc-method" className={INPUT} value={meta.method} onChange={(e) => setMeta({ ...meta, method: e.target.value })}>{METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </div>
            <div className="md:col-span-2"><label className={LABEL} htmlFor="rc-ref">Reference (UTR / cheque no.)</label><input id="rc-ref" className={INPUT} value={meta.reference} onChange={(e) => setMeta({ ...meta, reference: e.target.value })} /></div>
            <BankAccountFields meta={meta} setMeta={setMeta} idPrefix="rc" />
            <div><label className={LABEL} htmlFor="rc-tds">TDS deducted by customer</label><input id="rc-tds" type="number" min="0" step="0.01" className={INPUT} value={meta.tdsAmount || ''} onChange={(e) => setMeta({ ...meta, tdsAmount: e.target.value })} /></div>
            {Number(meta.tdsAmount) > 0 && <div><label className={LABEL} htmlFor="rc-tdss">TDS section</label><input id="rc-tdss" className={INPUT} placeholder="194J" value={meta.tdsSection || ''} onChange={(e) => setMeta({ ...meta, tdsSection: e.target.value.toUpperCase() })} /></div>}
          </div>

          {customerId && (invoices.length === 0 ? (
            <p className="text-[13px] text-[#706E6B] py-4 text-center">This customer has no unpaid invoices. You can still record the money below as an advance.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-52"><label className={LABEL} htmlFor="rc-spread">Amount received, oldest due first</label><input id="rc-spread" type="number" min="0" step="0.01" className={INPUT} value={spread} onChange={(e) => setSpread(e.target.value)} /></div>
                <button type="button" onClick={applySpread} className="h-9 px-3 rounded border border-[#C9C9C9] text-[12px] font-semibold">Apply</button>
                <button type="button" onClick={() => setAlloc(Object.fromEntries(invoices.map((i) => [i._id, String(balance(i))])))} className="h-9 px-3 rounded border border-[#C9C9C9] text-[12px] font-semibold">Settle all</button>
              </div>
              <div className="border border-[#D8DDE6] rounded overflow-x-auto">
                <table className="w-full text-[12px] sm:min-w-[40rem]">
                  <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide"><tr>
                    <th className="text-left font-semibold px-3 py-2">Invoice</th><th className="text-left font-semibold px-3 py-2">Due</th>
                    <th className="text-right font-semibold px-3 py-2">Total</th><th className="text-right font-semibold px-3 py-2">Balance</th>
                    <th className="text-right font-semibold px-3 py-2 w-40">Receive now</th>
                  </tr></thead>
                  <tbody>
                    {invoices.map((i) => (
                      <tr key={i._id} className="border-t border-[#F3F2F2]">
                        <td className="px-3 py-2"><span className="font-semibold">{i.invoiceNumber}</span>{i.installments?.length > 0 && <span className="ml-2 text-[10px] font-semibold bg-[#E0F2FE] text-[#0176D3] px-1.5 py-0.5 rounded">EMI</span>}</td>
                        <td className={cn('px-3 py-2', new Date(i.dueDate) < new Date() && 'text-[#BA0517] font-semibold')}>{fmtDate(i.dueDate)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(i.totalAmount, i.currency)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(balance(i), i.currency)}</td>
                        <td className="px-3 py-1.5"><input aria-label={`Amount for ${i.invoiceNumber}`} type="number" min="0" max={balance(i)} step="0.01" className={cn(INPUT, 'text-right')} value={alloc[i._id] || ''} onChange={(e) => setAlloc({ ...alloc, [i._id]: e.target.value })} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ))}
          {customerId && (
            <div className="flex flex-wrap items-end gap-3 border-t border-[#F3F2F2] pt-3">
              <div className="w-52"><label className={LABEL} htmlFor="rc-adv">Keep as advance</label><input id="rc-adv" type="number" min="0" step="0.01" className={INPUT} value={advance} onChange={(e) => setAdvance(e.target.value)} /></div>
              <p className="text-[12px] text-[#706E6B] flex-1 min-w-[200px]">Money received that isn't for an invoice yet (an advance, or more than is owed). It's held for this customer and can be applied to a later invoice or refunded.</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
          <span className="text-[13px]">Receipt total <strong className="tabular-nums">{formatCurrency(total, invoices[0]?.currency)}</strong>{Number(advance) > 0 && <span className="text-[#706E6B]"> · {formatCurrency(Number(advance), invoices[0]?.currency)} as advance</span>}{Number(meta.tdsAmount) > 0 && <span className="text-[#706E6B]"> · {formatCurrency(r2(total - Number(meta.tdsAmount)), invoices[0]?.currency)} into the bank after TDS</span>}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white">Cancel</button>
            <button type="button" disabled={saving || total <= 0} onClick={save} className="h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold disabled:opacity-60">{saving ? 'Saving…' : 'Record receipt'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
